import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { axiosInstance, SERVER_ORIGIN } from "../lib/axios";
import "./OperationsDashboard.css";

const isImage = (name = "") => /\.(jpg|jpeg|png|gif|webp)$/i.test(name);
const isVideo = (name = "") => /\.(mp4|webm|mov|avi|mkv)$/i.test(name);
const isPdf = (name = "") => /\.pdf$/i.test(name);

const fmtDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

const fmtTime = (date) =>
  date
    ? new Date(date).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const isSameDay = (date, target) => {
  if (!date) return false;
  const d = new Date(date);
  return (
    d.getFullYear() === target.getFullYear() &&
    d.getMonth() === target.getMonth() &&
    d.getDate() === target.getDate()
  );
};

const monthKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (key) => {
  if (!key) return "Select Month";
  const [year, month] = key.split("-");
  return new Date(`${year}-${month}-01`).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
};

const pickEmployeeName = (log) =>
  log?.employeeName ||
  log?.userName ||
  log?.user?.name ||
  log?.employee?.name ||
  log?.createdBy?.name ||
  log?.name ||
  "Employee";

const pickSenderName = (m) =>
  m?.sender?.name ||
  m?.sender?.username ||
  m?.senderLabel ||
  m?.senderName ||
  "Employee";

const getSenderId = (m) =>
  String(m?.sender?._id || m?.senderId || m?.userId || m?.createdBy || "");

const getProjectName = (p) =>
  p?.projectName || p?.name || p?.title || "Project";

const OperationsDashboard = ({ user }) => {
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const userId = String(user?._id || "");

  const [logs, setLogs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("today");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(false);

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);

      let logsData = [];

      if (isAdmin) {
        try {
          const res = await api.get("/logs/all", authHeader);
          logsData = Array.isArray(res.data) ? res.data : [];
        } catch {
          const res = await api.get("/logs/my", authHeader);
          logsData = Array.isArray(res.data) ? res.data : [];
        }
      } else {
        const res = await api.get("/logs/my", authHeader);
        logsData = Array.isArray(res.data) ? res.data : [];
      }

      setLogs(logsData);

      const projectRes = await axiosInstance.get("/projects");
      const projectList = Array.isArray(projectRes.data) ? projectRes.data : [];

      const neededProjectNames = new Set(
        logsData
          .map((l) =>
            String(l.projectName || "")
              .trim()
              .toLowerCase(),
          )
          .filter(Boolean),
      );

      const relatedProjects = projectList.filter((p) =>
        neededProjectNames.has(String(getProjectName(p)).trim().toLowerCase()),
      );

      const msgResults = await Promise.allSettled(
        relatedProjects.map(async (project) => {
          const res = await axiosInstance.get(
            `/project-messages/${project._id}/messages?limit=200`,
          );

          const arr = Array.isArray(res.data) ? res.data : [];

          return arr.map((m) => ({
            ...m,
            projectId: project._id,
            projectName: getProjectName(project),
          }));
        }),
      );

      let allMessages = msgResults
        .filter((r) => r.status === "fulfilled")
        .flatMap((r) => r.value || []);

      if (!isAdmin) {
        allMessages = allMessages.filter((m) => getSenderId(m) === userId);
      }

      setMessages(allMessages);
    } catch (err) {
      console.error("Operations dashboard error:", err);
      alert("Operations Dashboard data load failed.");
    } finally {
      setLoading(false);
    }
  };

  const monthOptions = useMemo(() => {
    const set = new Set();

    logs.forEach((l) => l.date && set.add(monthKey(l.date)));
    messages.forEach((m) => m.createdAt && set.add(monthKey(m.createdAt)));

    return [...set].sort().reverse();
  }, [logs, messages]);

  useEffect(() => {
    if (!selectedMonth && monthOptions.length) {
      setSelectedMonth(monthOptions[0]);
    }
  }, [monthOptions, selectedMonth]);

  const filteredLogs = useMemo(() => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (filter === "today") return logs.filter((l) => isSameDay(l.date, today));
    if (filter === "yesterday")
      return logs.filter((l) => isSameDay(l.date, yesterday));

    if (filter === "week") {
      const start = new Date();
      start.setDate(today.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      return logs.filter((l) => new Date(l.date) >= start);
    }

    if (filter === "month") {
      return logs.filter((l) => l.date && monthKey(l.date) === selectedMonth);
    }

    return logs;
  }, [logs, filter, selectedMonth]);

  const filteredMessages = useMemo(() => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (filter === "today")
      return messages.filter((m) => isSameDay(m.createdAt, today));

    if (filter === "yesterday")
      return messages.filter((m) => isSameDay(m.createdAt, yesterday));

    if (filter === "week") {
      const start = new Date();
      start.setDate(today.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      return messages.filter((m) => new Date(m.createdAt) >= start);
    }

    if (filter === "month") {
      return messages.filter(
        (m) => m.createdAt && monthKey(m.createdAt) === selectedMonth,
      );
    }

    return messages;
  }, [messages, filter, selectedMonth]);

  const groupedLogsByDay = useMemo(() => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (filter === "month" || filter === "week") {
      return {
        Yesterday: filteredLogs.filter((l) => isSameDay(l.date, yesterday)),
        Today: filteredLogs.filter((l) => isSameDay(l.date, today)),
      };
    }

    return {
      Yesterday: logs.filter((l) => isSameDay(l.date, yesterday)),
      Today: logs.filter((l) => isSameDay(l.date, today)),
    };
  }, [logs, filteredLogs, filter]);

  const groupedMessagesByDay = useMemo(() => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const makeGroup = (items) => {
      const map = {};
      items.forEach((m) => {
        const key = m.projectName || "No Project";
        if (!map[key]) map[key] = [];
        map[key].push(m);
      });
      return map;
    };

    return {
      Yesterday: makeGroup(
        filteredMessages.filter((m) => isSameDay(m.createdAt, yesterday)),
      ),
      Today: makeGroup(
        filteredMessages.filter((m) => isSameDay(m.createdAt, today)),
      ),
    };
  }, [filteredMessages]);

  const totals = useMemo(() => {
    return filteredLogs.reduce(
      (acc, l) => {
        acc.estimated += Number(l.estimatedHours || 0);
        acc.actual += Number(l.actualHours || 0);
        acc.entries += 1;
        return acc;
      },
      { estimated: 0, actual: 0, entries: 0 },
    );
  }, [filteredLogs]);

  const previewFile = async (fileKey) => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${SERVER_ORIGIN}/api/download?key=${encodeURIComponent(fileKey)}`,
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      if (!res.ok) return alert("Preview failed");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      console.error(err);
    }
  };

  const LogCard = ({ log }) => {
    const docs = Array.isArray(log.drawingDocument)
      ? log.drawingDocument.join(", ")
      : log.drawingDocument || log.drawingDocuments || "-";

    return (
      <div className="op-work-card op-compact-card">
        <div className="op-card-head">
          <div>
            <h4>📁 {log.projectName || "-"}</h4>
            <p>👤 {pickEmployeeName(log)}</p>
          </div>
          <span>{fmtDate(log.date)}</span>
        </div>

        <div className="op-pill-row">
          <span>🏗️ {log.typeofProject || "-"}</span>
          <span>📐 {log.workType || "-"}</span>
        </div>

        <div className="op-doc-line">📄 {docs}</div>

        <div className="op-hours-row">
          <div>
            <label>Estimate</label>
            <b>{Number(log.estimatedHours || 0).toFixed(2)} h</b>
          </div>
          <div>
            <label>Actual</label>
            <b>{Number(log.actualHours || 0).toFixed(2)} h</b>
          </div>
        </div>

        {log.remark && <div className="op-remark compact">{log.remark}</div>}
      </div>
    );
  };

  const MessageCard = ({ m }) => {
    const text = m.content || m.text || "";
    const fileName = m.fileName || "";

    return (
      <div className="op-work-card op-message-work-card op-compact-card">
        <div className="op-card-head">
          <div>
            <h4>📁 {m.projectName || "No Project"}</h4>
            <p>👤 {pickSenderName(m)}</p>
          </div>
          <span>
            {fmtDate(m.createdAt)} {fmtTime(m.createdAt)}
          </span>
        </div>

        {text && <div className="op-message-line">💬 {text}</div>}

        {m.file && (
          <div className="op-file-chip" onClick={() => previewFile(m.file)}>
            <span>
              {isImage(fileName)
                ? "🖼️"
                : isVideo(fileName)
                  ? "🎬"
                  : isPdf(fileName)
                    ? "📄"
                    : "📎"}
            </span>
            <b>{fileName || "Attachment"}</b>
          </div>
        )}
      </div>
    );
  };

  const renderDayColumn = (day) => {
    const msgCount = Object.values(groupedMessagesByDay[day] || {}).flat()
      .length;

    return (
      <div className="op-day-column" key={day}>
        <section className="op-section">
          <div className="op-section-title">
            <h3>{day} Work Detail</h3>
            <span>{groupedLogsByDay[day]?.length || 0} entries</span>
          </div>

          {groupedLogsByDay[day]?.length ? (
            groupedLogsByDay[day].map((log) => (
              <LogCard
                key={log._id || `${log.projectName}-${log.date}`}
                log={log}
              />
            ))
          ) : (
            <div className="op-empty">No work logs found.</div>
          )}
        </section>

        <section className="op-section">
          <div className="op-section-title">
            <h3>{day} Project Messages / Documents</h3>
            <span>{msgCount} items</span>
          </div>

          {Object.keys(groupedMessagesByDay[day] || {}).length ? (
            Object.entries(groupedMessagesByDay[day]).map(([project, list]) => (
              <div key={`${day}-${project}`}>
                {list.map((m) => (
                  <MessageCard
                    key={m._id || `${project}-${m.createdAt}`}
                    m={m}
                  />
                ))}
              </div>
            ))
          ) : (
            <div className="op-empty">No messages/documents found.</div>
          )}
        </section>
      </div>
    );
  };

  return (
    <div className="operations-dashboard">
      <div className="op-header">
        <div>
          <h2>Operations Dashboard</h2>
          <p>Employee work detail with project messages and documents.</p>
        </div>

        <div className="op-filter">
          {["yesterday", "today", "week"].map((f) => (
            <button
              key={f}
              className={filter === f ? "active" : ""}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}

          <select
            className={`op-month-select ${filter === "month" ? "active" : ""}`}
            value={selectedMonth}
            onChange={(e) => {
              setSelectedMonth(e.target.value);
              setFilter("month");
            }}
            onClick={() => setFilter("month")}
          >
            {monthOptions.length ? (
              monthOptions.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))
            ) : (
              <option value="">No Month</option>
            )}
          </select>
        </div>
      </div>

      <div className="op-summary">
        <div>
          <span>Total Entries</span>
          <b>{totals.entries}</b>
        </div>
        <div>
          <span>Actual Hours</span>
          <b>{totals.actual.toFixed(2)} h</b>
        </div>
        <div>
          <span>Estimate Hours</span>
          <b>{totals.estimated.toFixed(2)} h</b>
        </div>
        <div>
          <span>Difference</span>
          <b>{(totals.actual - totals.estimated).toFixed(2)} h</b>
        </div>
      </div>

      {loading ? (
        <div className="op-loading">Loading dashboard...</div>
      ) : (
        <div className="op-layout two-day-layout">
          {renderDayColumn("Yesterday")}
          {renderDayColumn("Today")}
        </div>
      )}
    </div>
  );
};

export default OperationsDashboard;
